import React, { ChangeEventHandler, memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CurrencyInput } from 'transfix-ui/components/CurrencyInput'
import { RadioGroup } from 'transfix-ui/components/RadioGroup/RadioGroup'
import { IFacilityAppointment } from 'types'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IDetentionShipment } from 'apps/detention-review/utils/types'
import {
  calculateRate,
  getEligibleTime,
  getEquipmentName,
  getMinutes,
} from 'utils/accessorialHelpers'

interface IRequestSummaryStore {
  shipmentData: IRequestSummaryState
}

interface IRequestSummaryState {
  shipment: IDetentionShipment
}

interface IRequestSummaryProps {
  startDateTime: string
  endDateTime: string
  detentionAmount: string
  calculatedAmount: number
  appointment?: IFacilityAppointment
  onChange?: (value: number) => void
  setDetentionHourlyRate: (value: number) => void
  editMode: boolean
  isAdmin: boolean
  canApproveRejectAccessorials?: boolean
}

const DETENTION_RATES = {
  FOURTY: '40',
  FIFTY: '50',
}

const detentionRateOptions = [
  { label: `${DETENTION_RATES.FOURTY}`, value: DETENTION_RATES.FOURTY },
  { label: `${DETENTION_RATES.FIFTY}`, value: DETENTION_RATES.FIFTY },
]

export const RequestSummary = memo<IRequestSummaryProps>(
  ({
    appointment,
    calculatedAmount,
    canApproveRejectAccessorials,
    detentionAmount,
    editMode,
    endDateTime,
    isAdmin,
    onChange,
    setDetentionHourlyRate,
    startDateTime,
  }) => {
    const state = useSelector<IRequestSummaryStore, IRequestSummaryState>(
      ({ shipmentData: { shipment } }) => ({ shipment })
    )
    const {
      shipment: { equipment },
    } = state

    const [detentionRate, setDetentionRate] = useState<string>(DETENTION_RATES.FIFTY)

    const minutes = useMemo(
      () =>
        getMinutes({
          startDateTime,
          endDateTime,
          appointment,
        }),
      [appointment, endDateTime, startDateTime]
    )

    const eligibleTime = useMemo(() => getEligibleTime(minutes), [minutes])
    const name = useMemo(() => getEquipmentName(equipment.parentId), [equipment])

    const amountAtFourty = useMemo(
      () => calculateRate(minutes, Number(DETENTION_RATES.FOURTY) / 4),
      [minutes]
    )

    const isRateFourty = useMemo(
      () => detentionAmount && amountAtFourty === Number(detentionAmount),
      [detentionAmount, amountAtFourty]
    )

    useEffect(() => {
      if (isRateFourty && detentionRate !== DETENTION_RATES.FOURTY) {
        setDetentionRate(DETENTION_RATES.FOURTY)
        setDetentionHourlyRate(Number(DETENTION_RATES.FOURTY))
      }
    }, [isRateFourty])

    const className = onChange ? 'row no-gutter' : 'row'

    const addToggle = useFeatureFlag('detentionPageAddToggle')
    const showToggle = addToggle && onChange && !editMode && canApproveRejectAccessorials

    const handleDetentionRateChange: ChangeEventHandler<HTMLInputElement> = e => {
      if (!onChange) {
        return
      }
      const rate = Number(e.target.value)
      const amount = calculateRate(minutes, rate / 4)
      setDetentionRate(e.target.value)
      setDetentionHourlyRate(rate)
      onChange(amount)
    }

    // set calculated amount inital value with rate when editing
    useEffect(() => {
      if (!onChange || editMode) {
        return
      }
      const amount = calculateRate(minutes, Number(detentionRate) / 4)
      onChange(amount)
    }, [editMode])

    return (
      <div>
        <div className={className}>
          <div className='col-md-6'>
            <strong>Detention-eligible time</strong>
          </div>
          <div className='col-md-6'>
            <strong className='fr' data-test='detention-eligible-time'>
              {eligibleTime}
            </strong>
          </div>
        </div>
        {isAdmin && (
          <div>
            {showToggle ? (
              <div className={className}>
                <div className='col-md-6'>
                  <strong>Rate per hour:</strong>
                </div>
                <div className='col-md-6'>
                  <div className='fr'>
                    <RadioGroup
                      name='detention-rate'
                      checked={detentionRate}
                      onOptionChange={handleDetentionRateChange}
                      label=''
                      required={false}
                      options={detentionRateOptions}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className={className}>
                <div className='col-md-6'>
                  <strong>Rate per hour: {name}</strong>
                </div>
                <div className='col-md-6'>
                  <strong className='fr'>${addToggle ? detentionRate : 50}</strong>
                </div>
              </div>
            )}

            <div className={className}>
              <div className='col-md-7'>
                <strong>Detention Payment:</strong>
              </div>
              <div className='col-md-5'>
                <div className='fr'>
                  {onChange && !editMode ? (
                    <CurrencyInput
                      value={calculatedAmount}
                      onChange={({ value }) => onChange(Number(value))}
                    />
                  ) : (
                    <strong>${detentionAmount}</strong>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
)
